export const DELEGATION_SCOPES = ['view', 'edit', 'submit_sso'] as const;
export type DelegationScope = (typeof DELEGATION_SCOPES)[number];

export function calendarDay(value: Date | string): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10);
  }
  const date = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function normalizeDelegationScopes(scopes: string[] | string | null | undefined): DelegationScope[] {
  const raw = Array.isArray(scopes) ? scopes.join(',') : String(scopes || '');
  const chosen = raw.split(',').map((part) => part.trim()).filter(Boolean);
  return DELEGATION_SCOPES.filter((scope) => chosen.includes(scope));
}

export function delegationValidationError(input: {
  userId?: string;
  reason?: string;
  startsAt?: string;
  endsAt?: string;
  scopes?: string[] | string;
}): string | null {
  if (!input.userId?.trim()) return 'Choose a person.';
  if (!input.reason || input.reason.trim().length < 3) return 'A reason is required.';
  const start = calendarDay(input.startsAt || '');
  const end = calendarDay(input.endsAt || '');
  if (!start || !end) return 'A start date and an end date are required.';
  if (end < start) return 'The end date must be on or after the start date.';
  if (normalizeDelegationScopes(input.scopes).length === 0) {
    return 'Choose a scope: view, edit, or submit SSO.';
  }
  return null;
}

export function delegationIsCurrent(
  row: { status?: string | null; starts_at?: string | null; ends_at?: string | null },
  today: string,
): boolean {
  if (String(row.status || 'active').toLowerCase() !== 'active') return false;
  const start = row.starts_at ? calendarDay(row.starts_at) : '';
  const end = row.ends_at ? calendarDay(row.ends_at) : '';
  if (start && today < start) return false;
  if (end && today > end) return false;
  return true;
}

export function delegationAllows(
  row: { status?: string | null; starts_at?: string | null; ends_at?: string | null; scope?: string | null },
  today: string,
  scope: DelegationScope,
): boolean {
  if (!delegationIsCurrent(row, today)) return false;
  const scopes = normalizeDelegationScopes(row.scope || 'view,submit_sso');
  return scopes.includes(scope);
}
